/**
 * The words on the camera-session check (CAM-08, Slice H).
 *
 * CAM-08 is a **measurement** — "≥80% of deliberate actions recognised after calibration; fewer
 * than three disruptive failures per session" — and it can only be taken by a person watching a
 * real child in a real room on real hardware. What Phase 3 owes it is the *instrumentation*: the
 * numbers a grown-up would otherwise have to tally by hand while also watching their child.
 *
 * Every string here is **invented** — no document writes a sentence for this surface — and every
 * one is in the grown-up's diagnostic register (D-040(3)): it sits behind a closed disclosure, no
 * child sees it by default, and nothing in it is a judgement of a child. It reports what the
 * *camera* managed, never what the child managed: "could not read a movement" is a fact about a
 * detector, and the whole point of the confidence gate (D-059 PS3-1) is that it never becomes a
 * fact about a five-year-old.
 */

export const INVENTED_CAMERA_ACTIVITY_COPY = {
  /** The closed disclosure's own label. */
  summary: 'Camera session check (for grown-ups)',
  /** What the recognised count is counting. */
  recognisedLabel: 'Movements the camera read',
  /** What the uncertainty count is counting. */
  uncertainLabel: 'Movements the camera could not read',
  /** Loss pauses, a stopped camera and the keyboard offer, together. */
  interruptionsLabel: 'Times the mission had to wait for the camera',
  /** The derived share, named as a share of what was observed. */
  shareLabel: 'Read as a share of the movements it saw',
  /** Said when there is nothing to divide yet. */
  shareUnavailable: 'not measured yet',
  /**
   * The honest bound on the share, said out loud where the number is.
   *
   * A movement the detector never noticed at all is in neither count, so this figure is an upper
   * bound rather than the CAM-08 measurement itself. The person in the room supplies the rest.
   */
  bound:
    'This counts what the camera read and what it knew it could not read. A movement it missed entirely is in neither number, so watch the room as well.',
  /**
   * The parent summary's heading for the same sitting-wide figure (MIN §21, GDS §33, Slice G′).
   *
   * Both documents list "Camera quality" beside the per-hero participation rows, and D-076(6) rules
   * how the two differ: participation is per child and neutral, camera quality is **one figure for
   * the sitting**. The heading therefore names the equipment, not a person and not a pair of people.
   */
  summaryHeading: 'How the camera did',
  /**
   * What the one figure covers, when it covers everybody (Slice G′).
   *
   * Said even in a single-player sitting, where it is trivially true, because a sentence that only
   * appears in the awkward case teaches a grown-up to read its absence as reassurance.
   */
  coverageEveryHero: 'These numbers cover every hero in this sitting.',
  /**
   * What the one figure covers in a **mixed** sitting (D-083(1), D-076(6)).
   *
   * The honesty obligation of the slice. One child answering with the keys contributes nothing to a
   * camera figure, and a grown-up who reads the figure as covering both children is being misled by
   * a number that is perfectly accurate. It names the wiring — which input a seat is using — and
   * never a child, never an ability, and never one child against the other.
   */
  coverageCameraHeroesOnly:
    'These numbers cover the heroes answering with the camera. A hero answering with the keys is not in them.',
} as const;

/** The whole inventory, for the copy audit. */
export const INVENTED_CAMERA_ACTIVITY_STRINGS: readonly string[] = Object.values(
  INVENTED_CAMERA_ACTIVITY_COPY,
);
